/**
 * PATH: packages/email-hub/src/feature/lib/imageProxy.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-IMAGEPROXY-001
 * PURPOSE: Fetch the bytes of a REMOTE email-body image (third-party
 *          CDN, e.g. a marketing hero image) through our OWN backend
 *          so the markup canvas / clipboard can read them.
 * WHY:     `fetchImageBlob` fetches the image `src` directly. That
 *          works for inline (cid:) attachments served from our origin
 *          and for the rare public host that emits
 *          `Access-Control-Allow-Origin: *`. It FAILS for the common
 *          case — a remote image on a host that sends no CORS headers
 *          for the dashboard origin — so the markup drawer showed
 *          "Couldn't load that image" for every remote image
 *          (BUG-2026-05-30-EMAIL-MARKUP-REMOTE-CORS). Routing the
 *          fetch through the same-origin, auth-gated, SSRF-guarded
 *          backend proxy (`/api/emails/image-proxy/`) removes the CORS
 *          constraint: the server fetches the bytes, the browser fetch
 *          is same-origin.
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────┐   ┌──────────┐
 *   │ remote src   │ → │ emailApiFetch       │ → │  Blob    │
 *   │ (no CORS)    │   │ /image-proxy/?url=  │   │ (or null)│
 *   └──────────────┘   └────────────────────┘   └──────────┘
 * DEPENDENCIES:
 *   - ./api emailApiFetch — credentialed master-backend fetch wrapper.
 * RELATED:
 *   - lib/imageClipboard.ts fetchImageBlob — direct-fetch first pass.
 *   - components/MarkupDrawer.tsx — caller (fallback on direct failure).
 *   - organisation/views/email_views/remote_image_proxy_views.py — BE.
 */

import { emailApiFetch } from './api'
import { isLikelyTrackingBeaconUrl } from './inlineImageFallback'

const PROXY_PATH = '/api/emails/image-proxy/'

/** Only http(s) absolute URLs are worth proxying. `data:` / `blob:`
 *  already work locally; `cid:` is rewritten to a same-origin signed
 *  URL upstream; relative URLs are same-origin so direct fetch already
 *  handled them. Analytics beacons are refused so the markup drawer
 *  cannot reintroduce the body-recovery beacon proxy path. Returns
 *  false for anything the proxy can't help with so we don't burn a
 *  backend round-trip on a hopeless src. */
export function isProxyableRemoteUrl(src: string): boolean {
  if (!src) return false
  if (isLikelyTrackingBeaconUrl(src)) return false
  try {
    const u = new URL(src, typeof window !== 'undefined' ? window.location.href : undefined)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

/** Fetch a remote image's bytes via the backend proxy. Returns a Blob
 *  on success, or ``null`` for any failure (non-proxyable src, 4xx/5xx
 *  from the proxy, network error). Never throws — the caller renders a
 *  single "couldn't load" error shape regardless of which strategy
 *  exhausted. */
export async function fetchImageViaProxy(src: string): Promise<Blob | null> {
  if (!isProxyableRemoteUrl(src)) return null
  try {
    const res = await emailApiFetch(`${PROXY_PATH}?url=${encodeURIComponent(src)}`)
    if (!res.ok) return null
    const blob = await res.blob()
    // Defend against a proxy that 200s with an empty/non-image body
    // (shouldn't happen — the BE forces image/* — but a misconfigured
    // gateway could). An empty blob would decode-fail downstream with
    // a more confusing error than "couldn't load".
    if (!blob || blob.size === 0) return null
    return blob
  } catch {
    return null
  }
}
